/**
 * Modal para distribuição de tempo entre múltiplas OS
 * 
 * @author Equipe GestaoeCRM
 * @date 21/10/2025
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Clock, X, Save, Plus, Trash2, AlertCircle } from 'lucide-react';
import { WorkSession, formatDuration } from '../services/workSessionApi';
import { osApi, sessionsApi } from '../services/api';

// ============================================================================
// INTERFACES
// ============================================================================

interface TimeDistributionEntry {
  id: string;
  osId: string;
  osTitle: string;
  startTime: string;
  endTime: string;
  duration: number; // em horas
}

interface TimeDistributionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (distributions: TimeDistributionEntry[]) => Promise<void>;
  session: WorkSession | null;
  loading?: boolean;
  /** Documento alvo para renderizar o modal (usado quando está no PIP) */
  targetDocument?: Document;
}

// ============================================================================
// COMPONENTE TIMEDISTRIBUTIONMODAL
// ============================================================================

/**
 * Modal para distribuir tempo entre múltiplas OS
 * 
 * Funcionalidades:
 * - Lista todas as OS disponíveis para o usuário
 * - Permite adicionar/remover OS para distribuição
 * - Campos de hora início e fim para cada OS
 * - Validação de tempo total não exceder tempo disponível
 * - Cálculo automático de duração
 */
export const TimeDistributionModal: React.FC<TimeDistributionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  session,
  loading = false,
  targetDocument,
}) => {
  const [availableOS, setAvailableOS] = useState<any[]>([]);
  const [distributions, setDistributions] = useState<TimeDistributionEntry[]>([]);
  const [totalDistributedTime, setTotalDistributedTime] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [loadingOS, setLoadingOS] = useState(false);

  // DEBUG: Log inicial do componente
  // console.log('🚀 TimeDistributionModal: Componente renderizado', {
  // isOpen,
  // session: !!session,
  // sessionData: session ? {
  // id: session.id,
  // startTime: session.startTime,
  // isActive: session.isActive,
  // isPaused: session.isPaused,
  // totalPausedTime: session.totalPausedTime,
  // pausedAt: session.pausedAt,
  // resumedAt: session.resumedAt,
  // duration: session.duration
  // } : null,
  // timestamp: new Date().toISOString()
  // });

  // DEBUG: Log específico para verificar se o código foi compilado
  // console.log('🔧 TimeDistributionModal: CÓDIGO ATUALIZADO - v0.33.52 - 21/10/2025');

  // Função para calcular duração da sessão (mesma lógica do StopTimerModal)
  const calculateSessionDuration = (session: WorkSession): number => {
    // console.log('🔍 TimeDistributionModal: Iniciando cálculo de duração...');
    
    if (!session?.startTime) {
      // console.log('❌ TimeDistributionModal: Sem startTime na sessão');
      return 0;
    }
    
    // console.log('✅ TimeDistributionModal: startTime encontrado:', session.startTime);
    
    const now = new Date();
    const startTime = new Date(session.startTime);
    
    // console.log('📅 TimeDistributionModal: Datas criadas:', {
    // now: now.toISOString(),
    // startTime: startTime.toISOString(),
    // nowTime: now.getTime(),
    // startTimeTime: startTime.getTime()
    // });
    
    // Verificar se as datas são válidas
    if (isNaN(startTime.getTime())) {
      // console.log('❌ TimeDistributionModal: startTime inválido:', session.startTime);
      return 0;
    }
    
    const totalMs = now.getTime() - startTime.getTime();
    const totalHours = totalMs / (1000 * 60 * 60);

    // console.log('⏱️ TimeDistributionModal: Cálculo inicial:', {
    // totalMs,
    // totalHours: totalHours.toFixed(4),
    // totalHoursFormatted: formatDuration(totalHours)
    // });

    // Calcular tempo de pausa atual se estiver pausada
    let currentPauseTime = 0;
    if (session.isPaused && session.pausedAt) {
      // console.log('⏸️ TimeDistributionModal: Sessão pausada, calculando pausa atual...');
      const pausedAt = new Date(session.pausedAt);
      if (!isNaN(pausedAt.getTime())) {
        const pauseMs = now.getTime() - pausedAt.getTime();
        currentPauseTime = Math.max(0, pauseMs / (1000 * 60 * 60));
        // console.log('⏸️ TimeDistributionModal: Pausa atual calculada:', {
        // pausedAt: pausedAt.toISOString(),
        // pauseMs,
        // currentPauseTime: currentPauseTime.toFixed(4)
        // });
      }
    }

    // Tempo total pausado acumulado (pausas anteriores)
    const totalPausedHours = session.totalPausedTime || 0;
    
    // console.log('📊 TimeDistributionModal: Tempos de pausa:', {
    // totalPausedHours: totalPausedHours.toFixed(4),
    // currentPauseTime: currentPauseTime.toFixed(4),
    // totalPausedFormatted: formatDuration(totalPausedHours),
    // currentPauseFormatted: formatDuration(currentPauseTime)
    // });
    
    // Duração real = tempo total - tempo pausado acumulado - pausa atual
    const workedHours = Math.max(0, totalHours - totalPausedHours - currentPauseTime);
    
    // console.log('🎯 TimeDistributionModal: Cálculo final:', {
    // formula: `${totalHours.toFixed(4)}h (total) - ${totalPausedHours.toFixed(4)}h (pausado) - ${currentPauseTime.toFixed(4)}h (pausa atual) = ${workedHours.toFixed(4)}h`,
    // workedHours: workedHours.toFixed(4),
    // workedHoursFormatted: formatDuration(workedHours),
    // sessionId: session.id,
    // isPaused: session.isPaused,
    // pausedAt: session.pausedAt
    // });
    
    return workedHours;
  };

  // Tempo total disponível para distribuição (em horas)
  // Usar o mesmo cálculo do StopTimerModal para consistência
  const totalAvailableTime = session ? calculateSessionDuration(session) : 0;
  
  // DEBUG: Log do tempo calculado
  // console.log('💰 TimeDistributionModal: Tempo total calculado:', {
  // totalAvailableTime,
  // formattedTime: formatDuration(totalAvailableTime),
  // sessionExists: !!session
  // });

  // Carregar OS disponíveis
  useEffect(() => {
    if (isOpen) {
      loadAvailableOS();
    }
  }, [isOpen]);

  // Calcular tempo total distribuído
  useEffect(() => {
    const total = distributions.reduce((sum, dist) => sum + dist.duration, 0);
    setTotalDistributedTime(total);
  }, [distributions]);

  // Validar distribuições
  useEffect(() => {
    validateDistributions();
  }, [distributions, totalAvailableTime]);

  const loadAvailableOS = async () => {
    try {
      setLoadingOS(true);
      const response = await osApi.getAll();
      if (response.data.success) {
        setAvailableOS(response.data.data || []);
      }
    } catch (error) {
      // console.error('Erro ao carregar OS:', error);
    } finally {
      setLoadingOS(false);
    }
  };

  const addDistribution = () => {
    const newEntry: TimeDistributionEntry = {
      id: Date.now().toString(),
      osId: '',
      osTitle: '',
      startTime: '',
      endTime: '',
      duration: 0,
    };
    setDistributions([...distributions, newEntry]);
  };

  const removeDistribution = (id: string) => {
    setDistributions(distributions.filter(dist => dist.id !== id));
  };

  const updateDistribution = (id: string, field: keyof TimeDistributionEntry, value: string | number) => {
    setDistributions(distributions.map(dist => {
      if (dist.id === id) {
        const updated = { ...dist, [field]: value };
        
        // Se mudou a OS, atualizar título
        if (field === 'osId') {
          const selectedOS = availableOS.find(os => os.id === value);
          updated.osTitle = selectedOS?.title || '';
        }
        
        // Se mudou horários, recalcular duração
        if (field === 'startTime' || field === 'endTime') {
          updated.duration = calculateDuration(updated.startTime, updated.endTime);
        }
        
        return updated;
      }
      return dist;
    }));
  };

  const calculateDuration = (startTime: string, endTime: string): number => {
    if (!startTime || !endTime) return 0;
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    if (end <= start) return 0;
    
    return (end.getTime() - start.getTime()) / (1000 * 60 * 60); // em horas
  };

  const validateDistributions = () => {
    const newErrors: string[] = [];
    
    // Verificar se tempo total não excede o disponível
    if (totalDistributedTime > totalAvailableTime) {
      newErrors.push(`Tempo total distribuído (${formatDuration(totalDistributedTime)}) excede o tempo disponível (${formatDuration(totalAvailableTime)})`);
    }
    
    // Verificar se todas as distribuições estão completas
    distributions.forEach((dist, index) => {
      if (!dist.osId) {
        newErrors.push(`OS ${index + 1}: Selecione uma OS`);
      }
      if (!dist.startTime) {
        newErrors.push(`OS ${index + 1}: Informe a hora de início`);
      }
      if (!dist.endTime) {
        newErrors.push(`OS ${index + 1}: Informe a hora de fim`);
      }
      if (dist.startTime && dist.endTime && dist.duration <= 0) {
        newErrors.push(`OS ${index + 1}: Hora de fim deve ser posterior à hora de início`);
      }
    });
    
    setErrors(newErrors);
  };

  const handleConfirm = async () => {
    if (errors.length > 0) return;
    
    try {
      // Calcular tempo restante não distribuído
      const remainingTime = totalAvailableTime - totalDistributedTime;
      
      // Se há tempo restante, adicionar automaticamente a OS de origem
      let finalDistributions = [...distributions];
      
      if (remainingTime > 0 && session?.orderOfServiceId) {
        // console.log('💰 TimeDistributionModal: Adicionando tempo restante à OS de origem:', {
        // remainingTime,
        // remainingFormatted: formatDuration(remainingTime),
        // osId: session.orderOfServiceId,
        // osTitle: session.orderOfService?.title || 'OS de origem'
        // });
        
        // Adicionar distribuição para a OS de origem com o tempo restante
        const originOSDistribution: TimeDistributionEntry = {
          id: 'origin-os',
          osId: session.orderOfServiceId,
          osTitle: session.orderOfService?.title || 'OS de origem',
          startTime: '', // Será calculado automaticamente
          endTime: '', // Será calculado automaticamente
          duration: remainingTime
        };
        
        // Calcular horários para a OS de origem baseado no tempo restante
        // Usar o horário de fim da sessão como referência
        const sessionEndTime = new Date();
        const sessionStartTime = new Date(sessionEndTime.getTime() - (remainingTime * 60 * 60 * 1000));
        
        originOSDistribution.startTime = sessionStartTime.toISOString().slice(0, 16);
        originOSDistribution.endTime = sessionEndTime.toISOString().slice(0, 16);
        
        finalDistributions.push(originOSDistribution);
        
        // console.log('✅ TimeDistributionModal: Distribuição final criada:', {
        // totalDistributions: finalDistributions.length,
        // totalTime: finalDistributions.reduce((sum, d) => sum + d.duration, 0),
        // remainingTime,
        // originOSDistribution
        // });
      }
      
      await onConfirm(finalDistributions);
      onClose();
    } catch (error) {
      // console.error('Erro ao distribuir tempo:', error);
    }
  };

  if (!isOpen || !session) {
    return null;
  }

  const modalContent = (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-background-glass backdrop-blur-glass border border-border-glass shadow-glass w-full max-w-4xl relative flex flex-col overflow-hidden max-h-[100dvh] sm:max-h-[90vh] rounded-none sm:rounded-button">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-secondary text-white p-6 rounded-none sm:rounded-t-button flex-none">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6" />
              <h3 className="text-xl font-semibold">Distribuir Tempo entre OS</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 flex-1 overflow-y-auto min-h-0">
          {/* Informações da Sessão */}
          <div className="mb-6">
            <h4 className="text-lg font-medium text-text-primary mb-2">
              Tempo Disponível para Distribuição
            </h4>
            <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20">
              <div className="flex justify-between items-center">
                <span className="text-text-muted font-bold">💰 Tempo Total:</span>
                <span className="font-mono text-xl font-bold text-green-400">
                  {(() => {
                    // console.log('🎨 TimeDistributionModal: Renderizando tempo total:', {
                    // totalAvailableTime,
                    // formatted: formatDuration(totalAvailableTime),
                    // session: !!session
                    // });
                    return formatDuration(totalAvailableTime);
                  })()}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm mt-1">
                <span className="text-text-muted">Tempo Distribuído:</span>
                <span className={`font-mono font-medium ${totalDistributedTime > totalAvailableTime ? 'text-red-400' : 'text-blue-400'}`}>
                  {(() => {
                    // console.log('🎨 TimeDistributionModal: Renderizando tempo distribuído:', {
                    // totalDistributedTime,
                    // formatted: formatDuration(totalDistributedTime)
                    // });
                    return formatDuration(totalDistributedTime);
                  })()}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-text-muted">Tempo Restante:</span>
                <span className={`font-mono font-medium ${(totalAvailableTime - totalDistributedTime) < 0 ? 'text-red-400' : 'text-green-400'}`}>
                  {(() => {
                    const remaining = Math.max(0, totalAvailableTime - totalDistributedTime);
                    // console.log('🎨 TimeDistributionModal: Renderizando tempo restante:', {
                    // totalAvailableTime,
                    // totalDistributedTime,
                    // remaining,
                    // formatted: formatDuration(remaining)
                    // });
                    return formatDuration(remaining);
                  })()}
                </span>
              </div>
              {(totalAvailableTime - totalDistributedTime) > 0 && (
                <div className="text-xs text-text-muted italic mt-1 text-center">
                  💡 Tempo restante será automaticamente registrado na OS de origem
                </div>
              )}
            </div>
          </div>

          {/* Lista de Distribuições */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h5 className="text-md font-medium text-text-primary">
                Distribuições de Tempo
              </h5>
              <button
                onClick={addDistribution}
                className="flex items-center space-x-2 px-3 py-2 bg-secondary/20 text-secondary rounded-button hover:bg-secondary/30 transition-colors text-sm font-medium border border-secondary/30"
              >
                <Plus className="w-4 h-4" />
                <span>Adicionar OS</span>
              </button>
            </div>

            {distributions.length === 0 ? (
              <div className="text-center py-8 text-text-muted">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma OS selecionada para distribuição</p>
                <p className="text-sm">Clique em "Adicionar OS" para começar</p>
              </div>
            ) : (
              <div className="space-y-4">
                {distributions.map((dist, index) => (
                  <div key={dist.id} className="bg-background-glass-hover rounded-button p-4 border border-border-glass/30">
                    <div className="flex justify-between items-start mb-4">
                      <h6 className="font-medium text-text-primary">
                        OS {index + 1}
                      </h6>
                      <button
                        onClick={() => removeDistribution(dist.id)}
                        className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Seleção de OS */}
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Ordem de Serviço *
                        </label>
                        <select
                          value={dist.osId}
                          onChange={(e) => updateDistribution(dist.id, 'osId', e.target.value)}
                          className="input"
                        >
                          <option value="">Selecione uma OS</option>
                          {availableOS.map(os => (
                            <option key={os.id} value={os.id}>
                              {os.title}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Duração Calculada */}
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Duração Calculada
                        </label>
                        <div className="px-3 py-2 bg-background-glass border border-border-glass rounded-button text-text-primary font-mono">
                          {formatDuration(dist.duration)}
                        </div>
                      </div>

                      {/* Hora de Início */}
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Hora de Início *
                        </label>
                        <input
                          type="datetime-local"
                          value={dist.startTime}
                          onChange={(e) => updateDistribution(dist.id, 'startTime', e.target.value)}
                          className="w-full px-3 py-2 border border-border-glass rounded-button shadow-glass focus:ring-2 focus:ring-secondary focus:border-secondary/50 bg-background-glass text-text-primary"
                        />
                      </div>

                      {/* Hora de Fim */}
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-2">
                          Hora de Fim *
                        </label>
                        <input
                          type="datetime-local"
                          value={dist.endTime}
                          onChange={(e) => updateDistribution(dist.id, 'endTime', e.target.value)}
                          className="w-full px-3 py-2 border border-border-glass rounded-button shadow-glass focus:ring-2 focus:ring-secondary focus:border-secondary/50 bg-background-glass text-text-primary"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Erros de Validação */}
            {errors.length > 0 && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-button p-4">
                <div className="flex items-start space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h6 className="font-medium text-red-400 mb-2">Erros de Validação:</h6>
                    <ul className="text-sm text-red-300 space-y-1">
                      {errors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-background-glass-hover border-t border-border-glass/30 rounded-none sm:rounded-b-button flex-none">
          <div className="flex justify-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border-glass rounded-button shadow-glass text-sm font-bold text-text-secondary bg-background-glass backdrop-blur-glass hover:bg-background-glass-hover"
            >
              Cancelar
            </button>
            
            <button
              type="button"
              onClick={handleConfirm}
              disabled={errors.length > 0 || distributions.length === 0 || loading}
              className={`
                flex items-center space-x-2 px-4 py-2 font-bold rounded-button transition-colors shadow-glass
                ${errors.length === 0 && distributions.length > 0 && !loading
                  ? 'bg-gradient-to-r from-green-500 to-green-600 hover:brightness-110 text-white'
                  : 'bg-background-glass-hover text-text-muted cursor-not-allowed border border-border-glass'
                }
              `}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{loading ? 'Distribuindo...' : 'Confirmar Distribuição'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Renderizar no documento correto
  const portalTarget = targetDocument?.body || document.body;
  
  return createPortal(modalContent, portalTarget);
};

export default TimeDistributionModal;
